/**
 * staff-page.tsx — Màn một nhân viên AI (`/ai/nhan-vien/:botId`), thay màn "Dạy AI" + form sửa cũ (khung ba tầng §6):
 *
 *   ĐẦU    = ảnh/emoji + tên (sửa tại chỗ), nhãn "Mặc định", trạng thái tự lưu, Lịch sử phiên bản, Phân tích (cùng tab, có Quay lại),
 *            Phát hành, Xoá. Nguyên tắc: màn khác mở TAB MỚI, chuyển mục trong màn này vẫn cùng tab (open-target-lib).
 *   TRẠNG THÁI = thanh "Đang trả lời / Chưa trả lời vì…" (status-bar.tsx, GET /ai/bots/:id/status) — nút sửa mở đúng mục
 *   TRÁI   = 6 mục + Nâng cao (hash `#vai-tro`, `#bo-nao`…), mỗi mục gắn nhãn "Cần phát hành" / "Hiệu lực ngay"
 *   GIỮA   = mục đang mở
 *   PHẢI   = khung chat thử (test-panel.tsx, chạy trên BẢN NHÁP). Dưới 1200px: nút nổi mở dạng ngăn kéo.
 *
 * Tự lưu (debounce) vào ai_bots: nhóm "Cách trả lời" vào bản nháp (khách dùng bản PHÁT HÀNH), nhóm "Vận hành" (bật/tắt, kênh,
 * giờ làm & chế độ, số khách/ngày) có hiệu lực ngay. AI bên thứ ba / Webhook: chỉ Bộ não (kết nối) + Giờ làm & kênh.
 */
import { useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  AlertTriangle, ArrowLeft, BarChart3, BookOpen, Brain, CalendarClock, Check, CircleDot, Cloud, CloudOff, Headset, History,
  Loader2, MessageSquareText, MoreHorizontal, Rocket, SlidersHorizontal, Star, Trash2, UserRound, Wand2, X,
} from 'lucide-react'
import { useFitHeight } from '@/components/ai/use-fit-height'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Loading, ErrorState } from '@/components/shared/feedback'
import { aiKeys, deleteAiBot, updateAiBot } from '@/hooks/use-ai'
import { useAiBotStatus } from '@/hooks/use-ai-bot-status'
import { TRAIN_BOT_RELOAD_EVENT } from '@/hooks/use-ai-train-test'
import { apiError } from '@/lib/api-client'
import { aiErrorMessage } from '@/lib/ai-credits'
import { AI_PATHS, isDefaultBot } from '@/lib/ai-khung'
import { cn } from '@/lib/utils'
import { currentLocale } from '@/lib/i18n'
import { avatarSrc } from '@/pages/settings/settings-utils'
import type { StatusSection } from '@/components/ai/bot-status-lib'
import { autosaveLabel, useAutosave } from '../train/use-autosave'
import { publishTrainBot, restoreTrainBot, saveTrainBot, trainKeys, useBotVersions, useTrainBot } from '../train/train-api'
import { toDraftForm, toInput, type DraftForm, type SectionProps } from '../train/draft-form'
import { PERSONA_MAX_CHARS, settingsProblems, type BotSettings } from '../train/bot-settings'
import { VersionsDrawer, PublishDialog } from '../train/versions-drawer'
import { TrainTestPanel } from '../train/test-panel'
import { RoleSection } from './section-role'
import { BrainSection } from './section-brain'
import { KnowledgeSection } from './section-knowledge'
import { SkillsSection } from './section-skills'
import { ToolsSection } from './section-tools'
import { HandoffSection } from './section-handoff'
import { OperationsSection } from './section-operations'
import { AdvancedSection } from './section-advanced'
import { StaffStatusBar } from './status-bar'
import { ScopeBadge } from './staff-ui'
import { useAiPerms } from '../use-ai-perms'
import { workHoursProblem } from '@/components/ai/work-hours-lib'
import {
  SCOPE_LABEL, STATUS_SECTION_ANCHOR, STATUS_SECTION_TAB, sectionFromHash, sectionsFor, type StaffSection,
} from './staff-lib'

const DEFAULT_EMOJI = '🤖'

/** Trang Phân tích của một nhân viên AI (giữ trang cũ). */
export const staffAnalyticsPath = (botId: string) => `/ai/nhan-vien/${botId}/phan-tich`

const SECTION_ICON: Record<StaffSection, typeof UserRound> = {
  'vai-tro': UserRound,
  'bo-nao': Brain,
  'kien-thuc': BookOpen,
  'ky-nang': Wand2,
  'chuyen-nguoi': Headset,
  'gio-lam': CalendarClock,
  'nang-cao': SlidersHorizontal,
}

export function AiStaffPage() {
  const { t } = useTranslation('aiStaff')
  const { botId } = useParams<{ botId: string }>()
  if (!botId) return <ErrorState message={t('page.missingId')} />
  return <StaffShell key={botId} botId={botId} />
}

function StaffShell({ botId }: { botId: string }) {
  const { t } = useTranslation('aiStaff')
  const navigate = useNavigate()
  const location = useLocation()
  const qc = useQueryClient()
  // Sửa / phát hành / xoá / chat thử nhân viên AI: backend đòi `ai.update` (ai-bot-routes, bot-version-routes,
  // simulate-routes). Không có quyền → chỉ xem: ẩn nút thao tác, ô nhập khoá.
  const { canEdit } = useAiPerms()

  const botQ = useTrainBot(botId)
  const versionsQ = useBotVersions(botId)
  const statusQ = useAiBotStatus(botId)

  const [form, setForm] = useState<DraftForm | null>(null)
  const [versionsOpen, setVersionsOpen] = useState(false)
  const [publishOpen, setPublishOpen] = useState(false)
  const [testOpen, setTestOpen] = useState(false)
  // Ba cột (mục · nội dung · chat thử) cao khít phần còn lại của màn hình, mỗi cột tự cuộn.
  const { ref: fitRef, height: fitHeight } = useFitHeight<HTMLDivElement>()
  const [editingName, setEditingName] = useState(false)

  const problems = useMemo(() => {
    if (!form) return []
    const hours = workHoursProblem({ ...form })
    return [...settingsProblems(form.settings), ...(hours ? [hours] : [])]
  }, [form])
  const personaTooLong = (form?.personaPrompt.length ?? 0) > PERSONA_MAX_CHARS

  const autosave = useAutosave<DraftForm>({
    value: form,
    save: async (patch) => {
      const res = await saveTrainBot(botId, toInput(patch))
      for (const w of res.warnings ?? []) toast.warning(w, { id: `bot-warn-${w}` })
      void qc.invalidateQueries({ queryKey: trainKeys.versions(botId) })
      // Danh sách + trạng thái (khoá con của aiKeys.bots) — kênh, giờ làm, bật/tắt đổi "Đang trả lời" ngay.
      void qc.invalidateQueries({ queryKey: aiKeys.bots })
    },
    canSave: (v) => settingsProblems(v.settings).length === 0 && !workHoursProblem({ ...v }) && v.personaPrompt.length <= PERSONA_MAX_CHARS && !!v.name.trim(),
    errorMessage: (err) => aiErrorMessage(err, apiError(err)),
  })
  const { reset: resetAutosave, flush } = autosave

  // Nạp bot lần đầu → mốc "đã lưu".
  useEffect(() => {
    // isFetchedAfterMount: chỉ nạp form từ bản vừa tải ở lần mở này, không từ bản đệm cũ.
    if (botQ.data && botQ.isFetchedAfterMount && !form) {
      const f = toDraftForm(botQ.data)
      setForm(f)
      resetAutosave(f)
    }
  }, [botQ.data, botQ.isFetchedAfterMount, form, resetAutosave])
  // Ngăn kéo chat thử (màn hẹp): Escape để đóng — trừ khi đang có hộp thoại mở (Escape thuộc về hộp đó).
  useEffect(() => {
    if (!testOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !document.querySelector('[role="dialog"]') && window.innerWidth < 1200) setTestOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [testOpen])
  // Rời màn (đổi trang, mở Kho tri thức…) → lưu nốt phần đang chờ.
  useEffect(() => () => { void flush() }, [flush])
  // Nháp bị sửa từ nơi khác (áp đề xuất Hướng dẫn AI, sửa kết nối) → lưu phần đang gõ rồi nạp lại bản mới từ server.
  useEffect(() => {
    const onReload = async (e: Event) => {
      if ((e as CustomEvent<{ botId?: string }>).detail?.botId !== botId) return
      await flush()
      const fresh = await botQ.refetch()
      if (fresh.data) { const f = toDraftForm(fresh.data); setForm(f); resetAutosave(f) }
      void qc.invalidateQueries({ queryKey: trainKeys.versions(botId) })
      void qc.invalidateQueries({ queryKey: aiKeys.bots })
    }
    window.addEventListener(TRAIN_BOT_RELOAD_EVENT, onReload)
    return () => window.removeEventListener(TRAIN_BOT_RELOAD_EVENT, onReload)
  }, [botId, botQ, flush, resetAutosave, qc])

  if (botQ.isError) return <ErrorState message={t('page.loadError')} />
  if (!form || !botQ.data) return <Loading label={t('page.loading')} />

  const bot = botQ.data
  const builtin = bot.kind === 'builtin'
  // Chat thử chạy AI sandbox (POST /ai/simulate/*, đòi `ai.update`) → không có quyền thì không hiện cột chat thử.
  const showTest = builtin && canEdit
  const isDefault = isDefaultBot(bot)
  const sections = sectionsFor(bot.kind)
  const section = sectionFromHash(location.hash, bot.kind)

  const set = (patch: Partial<DraftForm>) => setForm((f) => (f ? { ...f, ...patch } : f))
  const setSettings = (fn: (s: BotSettings) => BotSettings) => setForm((f) => (f ? { ...f, settings: fn(f.settings) } : f))
  const sectionProps: SectionProps = { botId, form, set, setSettings, canEdit }
  const versions = versionsQ.data
  const emoji = form.avatarEmoji || DEFAULT_EMOJI

  const goSection = (id: StaffSection, anchor?: string) => {
    navigate({ pathname: location.pathname, search: location.search, hash: id }, { replace: true })
    setTimeout(() => {
      const el = (anchor && document.getElementById(anchor)) || document.getElementById('staff-main')
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 60)
  }
  const openStatusSection = (s: Exclude<StatusSection, 'phat-hanh'>) => goSection(STATUS_SECTION_TAB[s], STATUS_SECTION_ANCHOR[s])

  const publish = async (note: string): Promise<boolean> => {
    const ok = await flush()
    if (!ok) { toast.error(t('page.draftNotSaved')); return false }
    try {
      const v = await publishTrainBot(botId, note)
      await Promise.all([
        qc.invalidateQueries({ queryKey: trainKeys.versions(botId) }),
        qc.invalidateQueries({ queryKey: aiKeys.bots }),
      ])
      toast.success(t('page.published', { version: v.version }))
      return true
    } catch (err) {
      toast.error(aiErrorMessage(err, apiError(err)))
      return false
    }
  }
  const restore = async (versionId: string, version: number) => {
    try {
      await flush()
      await restoreTrainBot(botId, versionId)
      const fresh = await botQ.refetch()
      if (fresh.data) { const f = toDraftForm(fresh.data); setForm(f); resetAutosave(f) }
      await Promise.all([
        qc.invalidateQueries({ queryKey: trainKeys.versions(botId) }),
        qc.invalidateQueries({ queryKey: aiKeys.bots }),
      ])
      toast.success(t('page.restored', { version }))
    } catch (err) {
      toast.error(apiError(err))
    }
  }
  const remove = async () => {
    if (!window.confirm(t('page.confirmDelete', { name: form.name }))) return
    try {
      await deleteAiBot(botId)
      await qc.invalidateQueries({ queryKey: aiKeys.bots })
      toast.success(t('page.deleted'))
      navigate(AI_PATHS.bots)
    } catch (err) {
      toast.error(aiErrorMessage(err, apiError(err)))
    }
  }

  const makeDefault = async () => {
    if (!window.confirm(t('page.confirmDefault', { name: form.name }))) return
    try {
      await flush()
      await updateAiBot(botId, { isDefault: true })
      window.dispatchEvent(new CustomEvent(TRAIN_BOT_RELOAD_EVENT, { detail: { botId } }))
      toast.success(t('page.madeDefault'))
    } catch (err) {
      toast.error(aiErrorMessage(err, apiError(err)))
    }
  }

  const st = autosave.status
  const saveIcon = st.state === 'saving' ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
    : st.state === 'error' ? <CloudOff className="h-3.5 w-3.5 text-destructive" />
    : st.state === 'pending' ? <CircleDot className="h-3.5 w-3.5 text-warning" />
    : <Cloud className="h-3.5 w-3.5 text-success" />

  const renderSection = () => {
    switch (section) {
      case 'vai-tro': return <RoleSection {...sectionProps} />
      case 'bo-nao': return <BrainSection {...sectionProps} bot={bot} />
      case 'kien-thuc': return <KnowledgeSection {...sectionProps} />
      case 'ky-nang': return (
        <>
          <SkillsSection {...sectionProps} />
          <ToolsSection {...sectionProps} onOpenSection={(id) => goSection(id)} />
        </>
      )
      case 'chuyen-nguoi': return <HandoffSection {...sectionProps} />
      case 'gio-lam': return <OperationsSection {...sectionProps} isDefault={isDefault} intakeToday={bot.intakeToday} />
      case 'nang-cao': return <AdvancedSection {...sectionProps} />
    }
  }

  return (
    <div className="flex flex-col gap-3">
      {/* ── Đầu trang ── */}
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => navigate(AI_PATHS.bots)} aria-label={t('page.backToList')}><ArrowLeft className="h-4 w-4" /></Button>
        {bot.avatarUrl ? (
          <img src={avatarSrc(bot.avatarUrl)} alt="" className="h-10 w-10 rounded-full object-cover" />
        ) : !canEdit ? (
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-xl" aria-hidden="true">{emoji}</span>
        ) : (
          <Input
            className="h-10 w-10 p-0 text-center text-xl" data-giu-co-chu
            value={form.avatarEmoji ?? ''} placeholder={DEFAULT_EMOJI}
            onChange={(e) => set({ avatarEmoji: e.target.value.slice(0, 4) || null })}
            aria-label={t('page.emojiAria')} title={t('page.emojiTitle')}
          />
        )}
        <div className="min-w-0">
          {editingName ? (
            <Input
              autoFocus className="h-8 w-56 text-sm font-semibold" maxLength={50} value={form.name}
              onChange={(e) => set({ name: e.target.value })}
              onBlur={() => setEditingName(false)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') setEditingName(false) }}
              aria-label={t('page.nameAria')}
            />
          ) : canEdit ? (
            <button type="button" className="max-w-[16rem] truncate rounded px-1 text-base font-semibold hover:bg-muted" onClick={() => setEditingName(true)} title={t('page.renameTitle')}>
              {form.name || t('page.unnamed')}
            </button>
          ) : (
            <span className="block max-w-[16rem] truncate px-1 text-base font-semibold">{form.name || t('page.unnamed')}</span>
          )}
          {!form.name.trim() && <p className="px-1 text-[11px] text-destructive">{t('page.nameRequired')}</p>}
        </div>
        {isDefault && <Badge variant="default" title={t('page.defaultTitle')}>{t('page.default')}</Badge>}
        <div className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground" title={st.state === 'error' ? st.message : undefined}>
          {saveIcon}
          <span className={cn('truncate', st.state === 'error' && 'text-destructive')}>{autosaveLabel(st)}</span>
        </div>
        {(problems.length > 0 || personaTooLong) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className="inline-flex items-center gap-1 rounded bg-warning/10 px-2 py-0.5 text-xs text-warning">
                <AlertTriangle className="h-3.5 w-3.5" /> {t('page.problems', { count: problems.length + (personaTooLong ? 1 : 0) })}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-80 p-2 text-xs">
              <ul className="list-disc space-y-1 pl-4">
                {personaTooLong && <li>{t('page.personaTooLong', { max: PERSONA_MAX_CHARS.toLocaleString(currentLocale()) })}</li>}
                {problems.map((p) => <li key={p}>{p}</li>)}
              </ul>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {builtin && versions && !versions.neverPublished && !versions.hasUnpublishedChanges && (
          <Badge variant="success" className="gap-1"><Check className="h-3 w-3" /> {t('page.customersUse', { version: versions.publishedVersion })}</Badge>
        )}

        <div className="ml-auto flex flex-wrap items-center gap-2">
          {builtin && <Button variant="outline" size="sm" onClick={() => setVersionsOpen(true)}><History className="h-4 w-4" /> {t('page.versionHistory')}</Button>}
          <Button variant="outline" size="sm" onClick={() => navigate(staffAnalyticsPath(botId))}><BarChart3 className="h-4 w-4" /> {t('page.analytics')}</Button>
          {builtin && canEdit && <Button size="sm" onClick={() => setPublishOpen(true)}><Rocket className="h-4 w-4" /> {t('page.publish')}</Button>}
          {!isDefault && canEdit && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label={t('page.moreActions')}><MoreHorizontal className="h-4 w-4" /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => void makeDefault()}><Star className="h-4 w-4" /> {t('page.makeDefault')}</DropdownMenuItem>
                <DropdownMenuItem className="text-destructive" onSelect={() => void remove()}><Trash2 className="h-4 w-4" /> {t('page.deleteBot')}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <StaffStatusBar
        botId={botId} status={statusQ.data} loading={statusQ.isLoading} error={statusQ.isError} onReload={() => statusQ.refetch()}
        onSection={openStatusSection} onPublish={() => setPublishOpen(true)} canEdit={canEdit && builtin}
      />

      <div ref={fitRef} style={fitHeight ? { height: fitHeight } : undefined} className={cn(
        'grid min-h-0 grid-cols-[minmax(0,1fr)] gap-3 min-[1200px]:grid-rows-[minmax(0,1fr)]',
        showTest ? 'min-[1200px]:grid-cols-[200px_minmax(0,1.4fr)_minmax(300px,0.75fr)]' : 'min-[1200px]:grid-cols-[200px_minmax(0,1fr)]',
      )}>
        {/* ══ TRÁI: các mục ══ */}
        <nav className="flex gap-1 overflow-x-auto rounded-xl border bg-background p-2 min-[1200px]:min-h-0 min-[1200px]:flex-col min-[1200px]:overflow-y-auto" aria-label={t('page.sectionsAria')}>
          {sections.map((s) => {
            const Icon = SECTION_ICON[s.id]
            const active = s.id === section
            return (
              <button key={s.id} type="button" onClick={() => goSection(s.id)} aria-current={active ? 'page' : undefined}
                className={cn('flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors', active ? 'bg-primary/10 font-medium text-primary' : 'hover:bg-muted')}>
                <Icon className="h-4 w-4 shrink-0" />
                <span className="flex-1 truncate">{s.label}</span>
                {s.scope === 'live' && <span className="hidden h-1.5 w-1.5 rounded-full bg-success min-[1200px]:inline-block" title={SCOPE_LABEL.live} />}
              </button>
            )
          })}
          <div className="hidden space-y-1 border-t px-2 pt-2 text-[11px] text-muted-foreground min-[1200px]:mt-auto min-[1200px]:block">
            <ScopeBadge scope="publish" /> <span className="block">{t('page.legendPublish')}</span>
            <ScopeBadge scope="live" className="mt-1" /> <span className="block">{t('page.legendLive')}</span>
          </div>
        </nav>

        {/* ══ GIỮA ══ */}
        <main id="staff-main" className="min-h-0 min-w-0 scroll-mt-4 space-y-3 overflow-y-auto pb-6 min-[1200px]:pr-1">
          {renderSection()}
        </main>

        {showTest && testOpen && (
          // Màn hẹp: lớp nền mờ sau ngăn kéo chat thử — bấm ra ngoài để đóng.
          <div className="fixed inset-0 z-30 bg-black/40 animate-fade-in min-[1200px]:hidden" onClick={() => setTestOpen(false)} aria-hidden="true" />
        )}

        {/* ══ PHẢI: chat thử (một bản duy nhất; màn hẹp thành ngăn kéo) ══ */}
        {showTest && (
          <section
            className={cn(
              'flex min-h-0 flex-col rounded-xl border bg-background p-3',
              'max-[1199px]:fixed max-[1199px]:inset-y-0 max-[1199px]:right-0 max-[1199px]:z-40 max-[1199px]:w-[min(420px,100vw)] max-[1199px]:rounded-none max-[1199px]:shadow-2xl',
              !testOpen && 'max-[1199px]:hidden',
            )}
            aria-label={t('page.testChat')}
          >
            <button type="button" className="mb-2 self-end rounded p-1 text-muted-foreground hover:bg-muted min-[1200px]:hidden" onClick={() => setTestOpen(false)} aria-label={t('page.closeTestChat')}>
              <X className="h-4 w-4" />
            </button>
            <p className="mb-2 text-[11px] text-muted-foreground"><Trans t={t} i18nKey="page.testChatNote" components={{ b: <b /> }} /></p>
            <TrainTestPanel botId={botId} botName={form.name || t('page.defaultBotName')} botEmoji={emoji} />
          </section>
        )}
      </div>

      {showTest && !testOpen && (
        <Button className="fixed bottom-5 right-5 z-30 rounded-full shadow-lg min-[1200px]:hidden" onClick={() => setTestOpen(true)}>
          <MessageSquareText className="h-4 w-4" /> {t('page.testChat')}
        </Button>
      )}

      {builtin && (
        <>
          <VersionsDrawer open={versionsOpen} onOpenChange={setVersionsOpen} botId={botId} botName={form.name} onRestore={restore} canEdit={canEdit} />
          <PublishDialog
            open={publishOpen}
            onOpenChange={setPublishOpen}
            onPublish={publish}
            hasChanges={!!versions?.hasUnpublishedChanges}
            neverPublished={!!versions?.neverPublished}
          />
        </>
      )}
    </div>
  )
}
